/**
 *  @class
 *  @function SlideShow
 */
if (!customElements.get('slide-show')) {
  class SlideShow extends HTMLElement {
    constructor() {
      super();
      const slideshow = this,
        dataset = slideshow.dataset;

      const slideshow_slides = Array.from(slideshow.querySelectorAll('.carousel__slide'));

      // Single early-return guard
      if (slideshow_slides.length < 1) return;

      const dots_enabled = dataset.dots === 'true',
        autoplay = dataset.autoplay == 'false' ? false : parseInt(dataset.autoplay, 10),
        align = dataset.align == 'center' ? 'center' : 'left',
        fade = dataset.fade == 'true',
        custom_dots = slideshow.querySelector('.flickity-page-dots'),
        animations = [],
        rightToLeft = document.dir === 'rtl',
        animations_enabled = document.body.classList.contains('animations-true') && typeof gsap !== 'undefined';

      // Cache classList checks — avoids repeated DOM reads throughout constructor
      const isImageTextImage   = slideshow.classList.contains('image-with-text-slideshow--image');
      const isImageTextContent = slideshow.classList.contains('image-with-text-slideshow--content');
      const isTestimonials     = slideshow.classList.contains('testimonials__carousel');
      const isCustomDots       = slideshow.classList.contains('custom-dots');
      const isMainSlideshow    = slideshow.classList.contains('main-slideshow');

      const args = {
        wrapAround: true,
        cellAlign: align,
        pageDots: dots_enabled,
        contain: true,
        fade,
        autoPlay: autoplay,
        rightToLeft,
        prevNextButtons: false,
        cellSelector: '.carousel__slide',
        on: {}
      };

      if (isImageTextImage) {
        const image_slideshow_slides = slideshow.querySelectorAll('.image-with-text-slideshow--image-media');
        args.draggable = false;
        if (image_slideshow_slides.length && image_slideshow_slides[0].classList.contains('desktop-height-auto')) {
          args.adaptiveHeight = true;
        }
      }
      if (isImageTextContent) {
        args.draggable = false;
        args.asNavFor = slideshow.parentNode.querySelector('.image-with-text-slideshow--image');
      }
      if (isImageTextContent || isTestimonials) {
        args.adaptiveHeight = true;
      }

      // Cache dot elements once — prevents querySelectorAll on every slide change
      const cached_dot_list = custom_dots ? Array.from(custom_dots.querySelectorAll('li')) : null;

      if (isCustomDots) {
        if (animations_enabled && isMainSlideshow) {
          this.prepareAnimations(slideshow, animations);
        }
        args.pauseAutoPlayOnHover = false;

        args.on = {
          staticClick: function () {
            this.unpausePlayer();
          },
          ready: function () {
            let flkty = this;

            if (animations_enabled && isMainSlideshow) {
              slideshow.animateSlides(0, animations);
              gsap.set(slideshow.querySelectorAll('.inline-badge,.subheading,.split-text,.button'), { visibility: 'visible' });
            }

            if (cached_dot_list) {
              cached_dot_list.forEach((dot, i) => {
                dot.addEventListener('click', () => flkty.select(i));
              });
              cached_dot_list[this.selectedIndex].classList.add('is-selected');
            }

            document.fonts.ready.then(() => flkty.resize());

            // Video Support.
            const video_container = flkty.cells[0].element.querySelector('.slideshow__slide-video-bg');
            if (video_container) {
              const iframe = video_container.querySelector('iframe');
              const video = video_container.querySelector('video');
              if (iframe) {
                iframe.onload = () => slideshow.videoPlay(video_container);
              } else if (video) {
                video.onloadstart = () => slideshow.videoPlay(video_container);
              }
            }
          },
          change: function (index) {
            flkty.cells[0].element.classList.remove('is-initial-selected');
            const previousIndex = fizzyUIUtils.modulo(this.selectedIndex - 1, this.slides.length);

            // Animations.
            if (animations_enabled && isMainSlideshow) {
              // gsap.delayedCall is GSAP-native and avoids a bare setTimeout magic number
              gsap.delayedCall(0.7, () => slideshow.animateReverse(previousIndex, animations));
              slideshow.animateSlides(index, animations);
            }

            // Color changes — single getComputedStyle call avoids two forced reflows
            const selectedElement = this.selectedElement;
            const cs = getComputedStyle(selectedElement);
            slideshow.style.setProperty('--color-body', cs.getPropertyValue('--color-body'));
            slideshow.style.setProperty('--color-body-rgb', cs.getPropertyValue('--color-body-rgb'));

            // Custom Dots.
            if (cached_dot_list) {
              cached_dot_list.forEach((dot) => dot.classList.remove('is-selected'));
              cached_dot_list[this.selectedIndex].classList.add('is-selected');
            }

            // AutoPlay
            if (autoplay) {
              this.stopPlayer();
              this.playPlayer();
            }

            // Video Support.
            const video_container_prev = flkty.cells[previousIndex].element.querySelector('.slideshow__slide-video-bg');
            if (video_container_prev) {
              slideshow.videoPause(video_container_prev);
            }

            const video_container = flkty.cells[index].element.querySelector('.slideshow__slide-video-bg');
            if (video_container) {
              // Cache iframe — avoids triple querySelector on the same element
              const iframe = video_container.querySelector('iframe');
              if (iframe) {
                if (iframe.classList.contains('lazyload')) {
                  iframe.addEventListener('lazybeforeunveil', () => slideshow.videoPlay(video_container));
                  lazySizes.loader.checkElems();
                } else {
                  slideshow.videoPlay(video_container);
                }
              } else if (video_container.querySelector('video')) {
                slideshow.videoPlay(video_container);
              }
            }
          }
        };
      }

      if (isMainSlideshow) {
        if (slideshow.classList.contains('desktop-height-image') || slideshow.classList.contains('mobile-height-image')) {
          args.adaptiveHeight = true;
        }
      }

      const flkty = new Flickity(slideshow, args);

      // Custom prev/next arrows rendered by the section (outside the Flickity viewport).
      const custom_nav = slideshow.querySelector('.slideshow__nav');
      if (custom_nav) {
        custom_nav.addEventListener('click', (event) => {
          const button = event.target.closest('.flickity-nav');
          if (!button) return;
          event.preventDefault();
          if (button.name === 'next') {
            flkty.next();
          } else {
            flkty.previous();
          }
          if (typeof flkty.unpausePlayer === 'function') {
            flkty.unpausePlayer();
          }
        });
      }

      dataset.initiated = true;

      if (Shopify.designMode) {
        slideshow.addEventListener('shopify:block:select', (event) => {
          const index = slideshow_slides.indexOf(event.target);
          flkty.select(index);
        });
      }
    }
    videoPause(video_container) {
      setTimeout(() => {
        if (video_container.dataset.provider === 'hosted') {
          video_container.querySelector('video').pause();
        } else {
          const iframe = video_container.querySelector('iframe');
          const message = iframe.dataset.provider === 'youtube' ? { event: "command", func: "pauseVideo", args: "" } : { method: "pause" };
          iframe.contentWindow.postMessage(JSON.stringify(message), "*");
        }
      }, 10);
    }
    videoPlay(video_container) {
      setTimeout(() => {
        if (video_container.dataset.provider === 'hosted') {
          video_container.querySelector('video').play();
        } else {
          const iframe = video_container.querySelector('iframe');
          const message = iframe.dataset.provider === 'youtube' ? { event: "command", func: "playVideo", args: "" } : { method: "play" };
          iframe.contentWindow.postMessage(JSON.stringify(message), "*");
        }
      }, 10);
    }
    prepareAnimations(slideshow, animations) {
      if (!slideshow.dataset.animationsReady) {
        // Cache NodeList — reused for both SplitText passes
        const split_text_els = slideshow.querySelectorAll('.split-text');
        new SplitText(split_text_els, { type: 'lines', linesClass: 'line-child' });
        new SplitText(split_text_els, { type: 'lines', linesClass: 'line-parent' });

        slideshow.querySelectorAll('.slideshow__slide').forEach((item, i) => {
          // Cache all element refs per slide — avoids repeated querySelector in timeline definitions
          const bg           = item.querySelector('.slideshow__slide-bg');
          const badge        = item.querySelector('.inline-badge');
          const subheading   = item.querySelector('.subheading');
          const headingLines = item.querySelectorAll('.slideshow__slide-content--heading .line-child');
          const bodyLines    = item.querySelectorAll('p:not(.subheading) .line-child');
          const buttons      = item.querySelectorAll('.button');

          const tl = gsap.timeline({ paused: true });
          animations[i] = tl;

          if (slideshow.dataset.transition == 'swipe') {
            tl.fromTo(item, {
              clipPath: 'polygon(0 0, 0 0, 0 100%, 0 100%)'
            }, {
              duration: 0.7,
              clipPath: 'polygon(0 0, 100% 0, 100% 100%, 0 100%)'
            }, "start");
          }

          tl
            .to(bg, { duration: 1, scale: 1 }, "start")
            .fromTo(badge,      { opacity: 0 }, { duration: 0.25, opacity: 1 }, '>-=0.5')
            .fromTo(subheading, { opacity: 0 }, { duration: 0.25, opacity: 1 }, '>-=0.3')
            .from(headingLines, { duration: 0.75, yPercent: 120, stagger: 0.1,  rotation: '2deg' }, '>-=0.3')
            .from(bodyLines,    { duration: 0.5,  yPercent: 120, stagger: 0.02, rotation: '2deg' }, '>-=0.3')
            .fromTo(buttons, { yPercent: 120, rotation: '2deg' }, { duration: 0.5, yPercent: 0, rotation: 0, stagger: 0.1 }, '>-=0.3');
        });
        slideshow.dataset.animationsReady = true;
      }
    }
    animateSlides(i, animations) {
      animations[i].timeScale(1).restart();
    }
    animateReverse(i, animations) {
      animations[i].timeScale(4).reverse();
    }
  }
  customElements.define('slide-show', SlideShow);
}
